import { FormEvent, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { SearchableMultiSelect } from '../ui/SearchableMultiSelect';
import { Checkbox } from '../ui/Checkbox';
import { RadioGroup } from '../ui/RadioGroup';
import { ErrorAlert } from '../ui/ErrorAlert';
import { KnowledgeLink } from '../ui/KnowledgeLink';
import { extractValidationErrors, getErrorMessage } from '@/utils/errorHandler';
import type {
    UserSettings,
    UpdateUserSettingsData,
    CurrencyDisplay,
    CurrencyPosition,
    UserSettingsOptionsResponse,
} from '@/api/types';

interface UserSettingsFormProps {
    settings: UserSettings;
    availableCurrencies: Record<string, string>;
    options: UserSettingsOptionsResponse;
    onSubmit: (data: UpdateUserSettingsData) => Promise<void>;
    isLoading?: boolean;
}

export function UserSettingsForm({
    settings,
    availableCurrencies,
    options,
    onSubmit,
    isLoading = false,
}: UserSettingsFormProps) {
    // Currency Preferences
    const [currencies, setCurrencies] = useState<string[]>(settings.currencies);
    const [showNetWorthInSdr, setShowNetWorthInSdr] = useState(settings.showNetWorthInSdr);

    // Number Formatting (use max as the single source - min/max will be set to same value)
    const [fractionDigits, setFractionDigits] = useState(
        settings.maximumFractionDigits.toString(),
    );
    const [thousandsSeparator, setThousandsSeparator] = useState(settings.thousandsSeparator);
    const [decimalSeparator, setDecimalSeparator] = useState(settings.decimalSeparator);
    const [useGrouping, setUseGrouping] = useState(settings.useGrouping);

    // Display Options
    const [currencyDisplay, setCurrencyDisplay] = useState<CurrencyDisplay>(
        settings.currencyDisplay,
    );
    const [currencyPosition, setCurrencyPosition] = useState<CurrencyPosition>(
        settings.currencyPosition,
    );

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);

    // Filter out SDR from available currencies (user selects it via checkbox)
    const currencyOptions = Object.entries(availableCurrencies)
        .filter(([code]) => code !== 'SDR')
        .map(([code, name]) => ({ value: code, label: `${code} - ${name}` }));

    // Convert API options to component format
    const currencyDisplayOptions = Object.entries(options.currencyDisplay).map(([value, label]) => ({
        value: value as CurrencyDisplay,
        label,
        description:
            value === 'symbol'
                ? 'Display currency as symbol (e.g., $, €)'
                : 'Display currency as ISO code (e.g., USD, EUR)',
    }));

    const currencyPositionOptions = Object.entries(options.currencyPosition).map(
        ([value, label]) => ({
            value: value as CurrencyPosition,
            label,
            description:
                value === 'before'
                    ? 'Show currency before amount (e.g., $100)'
                    : 'Show currency after amount (e.g., 100€)',
        }),
    );

    const thousandsSeparatorOptions = Object.entries(options.thousandsSeparator).map(
        ([value, label]) => ({
            value,
            label,
        }),
    );

    const decimalSeparatorOptions = Object.entries(options.decimalSeparator).map(
        ([value, label]) => ({
            value,
            label,
        }),
    );

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});
        setGeneralError(null);

        // Validation
        const digits = parseInt(fractionDigits);

        if (
            isNaN(digits) ||
            digits < options.fractionDigits.min ||
            digits > options.fractionDigits.max
        ) {
            setErrors({
                fractionDigits: `Must be between ${options.fractionDigits.min} and ${options.fractionDigits.max}`,
            });
            return;
        }

        if (currencies.length === 0) {
            setErrors({ currencies: 'Please select at least one currency' });
            return;
        }

        if (thousandsSeparator && thousandsSeparator === decimalSeparator) {
            setErrors({
                thousandsSeparator: 'Thousands and decimal separators must be different',
            });
            return;
        }

        try {
            const data: UpdateUserSettingsData = {
                currencies,
                showNetWorthInSdr,
                currencyDisplay,
                currencyPosition,
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
                thousandsSeparator,
                decimalSeparator,
                useGrouping,
            };

            await onSubmit(data);
        } catch (error) {
            const validationErrors = extractValidationErrors(error);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
            } else {
                setGeneralError(getErrorMessage(error));
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
            {generalError && (
                <ErrorAlert message={generalError} onDismiss={() => setGeneralError(null)} />
            )}

            {/* Section 1: Currencies */}
            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-dark">
                <h3 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
                    Currencies
                </h3>
                <div className="space-y-4">
                    <SearchableMultiSelect
                        label="Active Currencies"
                        value={currencies}
                        onChange={setCurrencies}
                        options={currencyOptions}
                        error={errors.currencies}
                        helperText="Select currencies you want to track"
                        placeholder="Select currencies..."
                    />
                    <Checkbox
                        label={
                            <span>
                                Show Net Worth in{' '}
                                <KnowledgeLink page="sdr">SDR</KnowledgeLink>
                            </span>
                        }
                        checked={showNetWorthInSdr}
                        onChange={(e) => setShowNetWorthInSdr(e.target.checked)}
                        helperText="Add Special Drawing Rights to currency selector"
                    />
                </div>
            </section>

            {/* Section 2: Number Formatting */}
            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-dark">
                <h3 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
                    Number Formatting
                </h3>
                <div className="space-y-4">
                    <div className="flex flex-wrap items-start gap-6">
                        <div className="w-32">
                            <Input
                                label="Decimals"
                                type="number"
                                min={options.fractionDigits.min}
                                max={options.fractionDigits.max}
                                value={fractionDigits}
                                onChange={(e) => setFractionDigits(e.target.value)}
                                error={errors.fractionDigits}
                            />
                        </div>
                        <div className="w-40">
                            <Select
                                label="Thousands"
                                value={thousandsSeparator}
                                onChange={setThousandsSeparator}
                                options={thousandsSeparatorOptions}
                                error={errors.thousandsSeparator}
                            />
                        </div>
                        <div className="w-40">
                            <Select
                                label="Decimal"
                                value={decimalSeparator}
                                onChange={setDecimalSeparator}
                                options={decimalSeparatorOptions}
                                error={errors.decimalSeparator}
                            />
                        </div>
                    </div>
                    <Checkbox
                        label="Use digit grouping"
                        checked={useGrouping}
                        onChange={(e) => setUseGrouping(e.target.checked)}
                        helperText="Display as 1,000,000 instead of 1000000"
                    />
                </div>
            </section>

            {/* Section 3: Currency Display */}
            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-dark">
                <h3 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
                    Currency Display
                </h3>
                <div className="flex flex-wrap gap-8">
                    <RadioGroup
                        label="Format"
                        value={currencyDisplay}
                        onChange={setCurrencyDisplay}
                        options={currencyDisplayOptions}
                        error={errors.currencyDisplay}
                    />
                    <RadioGroup
                        label="Position"
                        value={currencyPosition}
                        onChange={setCurrencyPosition}
                        options={currencyPositionOptions}
                        error={errors.currencyPosition}
                    />
                </div>
            </section>

            {/* Submit Button */}
            <div className="pt-2">
                <Button type="submit" variant="primary" isLoading={isLoading}>
                    Save Settings
                </Button>
            </div>
        </form>
    );
}
